import "@/index.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider } from "./context/AuthContext";
import { SiteSettingsProvider } from "./context/SiteSettingsContext";
import { Toaster } from "./components/ui/sonner";
import { FloatingChatButton } from "./components/EmeraldAIModal";
import { ScrollToTop } from "./components/ScrollToTop";

// Pages
import HomePage from "./pages/HomePage";
import RoomsPage from "./pages/RoomsPage";
import DiningPage from "./pages/DiningPage";
import GalleryPage from "./pages/GalleryPage";
import ExplorePage from "./pages/ExplorePage";
import OffersPage from "./pages/OffersPage";
import EventsPage from "./pages/EventsPage";
import ContactPage from "./pages/ContactPage";
import BookingPage from "./pages/BookingPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import EmeraldAIPage from "./pages/EmeraldAIPage";
import ItineraryPage from "./pages/ItineraryPage";
import LovelySisPage from "./pages/LovelySisPage";
import DynamicPage from "./pages/DynamicPage";

// Admin Pages
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminRooms from "./pages/admin/AdminRooms";
import AdminInquiries from "./pages/admin/AdminInquiries";
import AdminEventInquiries from "./pages/admin/AdminEventInquiries";
import AdminTestimonials from "./pages/admin/AdminTestimonials";
import AdminOffers from "./pages/admin/AdminOffers";
import AdminNewsletter from "./pages/admin/AdminNewsletter";
import AdminBlog from "./pages/admin/AdminBlog";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminTripLeads from "./pages/admin/AdminTripLeads";
import AdminBookingInquiries from "./pages/admin/AdminBookingInquiries";
import AdminGallery from "./pages/admin/AdminGallery";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminMenu from "./pages/admin/AdminMenu";
import AdminPages from "./pages/admin/AdminPages";
import AdminDining from "./pages/admin/AdminDining";
import { AdminLayout } from "./components/admin/AdminLayout";

// Show Floating Chat Button only on public pages (not admin)
function ConditionalFloatingChat() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  if (isAdminRoute) return null;
  return <FloatingChatButton />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SiteSettingsProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/rooms" element={<RoomsPage />} />
              <Route path="/dining" element={<DiningPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/explore" element={<ExplorePage />} />
              <Route path="/offers" element={<OffersPage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/book" element={<BookingPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/emerald-ai" element={<EmeraldAIPage />} />
              <Route path="/itinerary/:id" element={<ItineraryPage />} />
              <Route path="/tours" element={<LovelySisPage />} />
              
              {/* Dynamic Page Route - for custom CMS pages */}
              <Route path="/p/:slug" element={<DynamicPage />} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="pages" element={<AdminPages />} />
                <Route path="menu" element={<AdminMenu />} />
                <Route path="rooms" element={<AdminRooms />} />
                <Route path="inquiries" element={<AdminInquiries />} />
                <Route path="events" element={<AdminEventInquiries />} />
                <Route path="testimonials" element={<AdminTestimonials />} />
                <Route path="offers" element={<AdminOffers />} />
                <Route path="newsletter" element={<AdminNewsletter />} />
                <Route path="blog" element={<AdminBlog />} />
                <Route path="dining" element={<AdminDining />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="trip-leads" element={<AdminTripLeads />} />
                <Route path="booking-inquiries" element={<AdminBookingInquiries />} />
                <Route path="gallery" element={<AdminGallery />} />
                <Route path="users" element={<AdminUsers />} />
              </Route>
            </Routes>
            <ConditionalFloatingChat />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </SiteSettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
